import { PERMISSIONS, type Actor, type Permission, type Role } from './types';

/**
 * Rights of a person = defaults of the role, minus the defaults taken away for this person, plus rights granted
 * individually. Project-level rights (project lead) are derived from project membership, not from these presets.
 */

/**
 * Rights that open business or personal data beyond the technical admin role: approvals and corrections,
 * month closing, billing, sales and cost rates, HR data (contract hours, absences, notes), team figures,
 * workload planning, the complete export, the project analysis of all projects, customers, announcements and the
 * project administration (it assigns project leads, and a project lead sees the hours of the project). Only someone
 * who holds such a right can grant it (role or individual right) or change an account that holds it (role, rights,
 * deactivation, e-mail, access reset). Otherwise the technical admin could take such an account over (reset, then
 * sign in with the setup link) or make itself project lead, and read what the admin role deliberately excludes.
 */
export const PROTECTED_PERMISSIONS: readonly Permission[] = [
  'time.approve.final',
  'time.manage',
  'period.manage',
  'billing.manage',
  'rates.sales.view',
  'rates.cost.view',
  'hr.manage',
  'kpi.team.view',
  'workload.manage',
  'data.export.all',
  'analytics.view',
  'customers.manage',
  'announcements.manage',
  'projects.manage',
  'projects.view.all',
];

/**
 * Protected rights in `perms` that the actor does not hold. Rights switched off for everybody by a company setting (team
 * figures) still count as held here: switching a feature off must not stop management from managing its own accounts
 * (DATA-10, review of 23.10.2026); reading the feature stays closed through `permissions`.
 */
export function missingProtected(actor: Actor, perms: Iterable<Permission>): Permission[] {
  const held = actor.grantable ?? actor.permissions;
  const out: Permission[] = [];
  for (const p of perms) if ((PROTECTED_PERMISSIONS as readonly string[]).includes(p) && !held.has(p)) out.push(p);
  return out;
}

/** Business rights of the role "Büro und Geschäftsführung": everything the management works with. */
const OFFICE_RIGHTS: readonly Permission[] = [
  'time.approve.final',
  'time.manage',
  'period.manage',
  'billing.manage',
  'rates.sales.view',
  'rates.cost.view',
  'hr.manage',
  'kpi.team.view',
  'workload.manage',
  'projects.manage',
  'users.manage',
  'audit.view',
  'data.export.all',
  'analytics.view',
  'customers.manage',
  'announcements.manage',
  'projects.view.all',
  'staffing.view',
];

/**
 * Default rights of each role. They are pre-set for every person of the role and can be taken away (or other rights
 * added) per person under Personen > Rolle und Rechte.
 * - employee: "Wer arbeitet wo" (who is in which active project, without hours; round 5, request 19); booking, the own
 *   week, own absences and the team calendar need no right.
 * - external: no rights. External partners book only on the projects (and tasks) they are assigned to and do not see
 *   the team; "Wer arbeitet wo" only when the office grants it.
 * - office "Büro und Geschäftsführung": all business rights, so that the office and the assistant of the management
 *   work with the same data as the management. The right to give rights ("rights.manage") is not part of it: the
 *   management holds it individually (and may hand it on), the office does not.
 * - admin "Technischer Admin": accounts, rights (no protected ones), settings, Forma synchronisation, backup and the
 *   audit log (amounts, hours and health data in it only with the matching right), "Wer arbeitet wo" without hours. No
 *   approvals, billing, HR data, personal figures and no project administration (it would assign project leads).
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  employee: ['staffing.view'],
  external: [],
  office: OFFICE_RIGHTS,
  admin: ['users.manage', 'rights.manage', 'settings.manage', 'sync.manage', 'backup.manage', 'audit.view', 'staffing.view'],
};

const known = (p: string): p is Permission => (PERMISSIONS as readonly string[]).includes(p);

/** Effective rights: defaults of the role minus `removed` plus `extra` (unknown names are ignored). */
export function effectivePermissions(role: Role, extra: readonly string[] = [], removed: readonly string[] = []): Set<Permission> {
  const gone = new Set(removed);
  const set = new Set<Permission>((ROLE_PERMISSIONS[role] ?? []).filter((p) => !gone.has(p)));
  for (const p of extra) if (known(p)) set.add(p);
  return set;
}

/**
 * Splits a wanted set of rights of a person into what differs from the role: rights added on top of the defaults and
 * defaults taken away. Stored like that, a person keeps future defaults of the role that nobody took away.
 */
export function rightsDelta(role: Role, wanted: Iterable<string>): { extra: Permission[]; removed: Permission[] } {
  const want = new Set([...wanted].filter(known));
  const defaults = new Set<Permission>(ROLE_PERMISSIONS[role] ?? []);
  return {
    extra: [...want].filter((p) => !defaults.has(p)).sort(),
    removed: [...defaults].filter((p) => !want.has(p)).sort(),
  };
}

export function can(actor: Actor, permission: Permission): boolean {
  return actor.permissions.has(permission);
}

export function canAny(actor: Actor, ...perms: Permission[]): boolean {
  return perms.some((p) => actor.permissions.has(p));
}

/** Sees all projects (not only memberships). The technical admin (Forma synchronisation) sees the list, not the hours. */
export function seesAllProjects(actor: Actor): boolean {
  return canAny(actor, 'projects.manage', 'projects.view.all', 'time.manage', 'time.approve.final', 'billing.manage', 'kpi.team.view', 'workload.manage', 'analytics.view', 'sync.manage');
}

export function isLeadOf(actor: Actor, projectId: string): boolean {
  return actor.leadProjectIds.has(projectId);
}

export function isMemberOf(actor: Actor, projectId: string): boolean {
  return actor.memberProjectIds.has(projectId);
}
